/**
 * Pure state helpers for TTRouter's browser-history and in-memory request contracts.
 *
 * Keeping these operations independent of Vue and browser globals makes boundary
 * behavior deterministic while TTRouter remains the owner of all mutations.
 */

export const APP_HISTORY_DEPTH_STATE_KEY = 'tt_app_history_depth';

/**
 * Return the number of Back steps owned by the current TimeTrex history entry.
 *
 * Missing or malformed state fails closed at the application boundary. Falling
 * back to history.length could navigate outside TimeTrex after the user went Back.
 */
export function getAppHistoryDepth( history_state ) {
    const depth = history_state?.[APP_HISTORY_DEPTH_STATE_KEY];

    return Number.isInteger( depth ) && depth >= 0 ? depth : 0;
}

/**
 * Merge TimeTrex history depth into Vue Router's state without replacing its cursor fields.
 */
export function buildAppHistoryState( history_state, depth ) {
    return {
        ...( history_state && typeof history_state === 'object' ? history_state : {} ),
        [APP_HISTORY_DEPTH_STATE_KEY]: Number.isInteger( depth ) && depth >= 0 ? depth : 0,
    };
}

/**
 * Keep the existing compact `t` route parameter unique even when navigations overlap.
 */
export function getNextTimestampParam( current_timestamp, previous_timestamp ) {
    const normalized_current = Number.isInteger( current_timestamp ) ? current_timestamp : 0;
    const normalized_previous = Number.isInteger( previous_timestamp ) ? previous_timestamp : 0;

    return Math.max( normalized_current, normalized_previous + 1 );
}

/**
 * Normalize route syntax while retaining every query parameter used as request identity.
 */
export function getNavigationRequestKey( path ) {
    if ( typeof path !== 'string' || path.length === 0 ) {
        return '';
    }

    if ( path.startsWith( '#/' ) ) {
        path = path.slice( 1 );
    } else if ( path.startsWith( '#' ) ) {
        path = '/' + path.slice( 1 );
    } else if ( !path.startsWith( '/' ) ) {
        path = '/' + path;
    }

    return path;
}
